/**
 * Comprehensive validation utilities for notification system
 * Requirements: 9.1, 9.2 - Error handling and validation
 */

import { PreReleaseProject } from '../services/preReleaseService';
import { InterestedUser, EmailTemplateData, WebsiteMessageData } from '../services/postReleaseNotificationService';

export class NotificationValidationError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly field?: string,
    public readonly details?: any
  ) {
    super(message);
    this.name = 'NotificationValidationError';
  }
}

export class NotificationServiceError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly operation?: string,
    public readonly details?: any
  ) {
    super(message);
    this.name = 'NotificationServiceError';
  }
}

export class NotificationValidation {
  /**
   * Validate project state for notifications
   */
  static validateProjectForNotifications(project: PreReleaseProject): void {
    if (!project) {
      throw new NotificationValidationError(
        'Project is required for notification operations',
        'PROJECT_REQUIRED'
      );
    }

    if (!project.id) {
      throw new NotificationValidationError(
        'Project ID is required',
        'PROJECT_ID_REQUIRED',
        'id'
      );
    }

    if (!project.title?.trim()) {
      throw new NotificationValidationError(
        'Project title is required',
        'PROJECT_TITLE_REQUIRED',
        'title'
      );
    }

    if (project.status !== 'released') {
      throw new NotificationValidationError(
        'Project must be in "released" status to send notifications',
        'INVALID_PROJECT_STATUS',
        'status',
        { currentStatus: project.status, requiredStatus: 'released' }
      );
    }

    if (!project.publisher_id) {
      throw new NotificationValidationError(
        'Project publisher ID is required',
        'PUBLISHER_ID_REQUIRED',
        'publisher_id'
      );
    }
  }

  /**
   * Validate interested users list
   */
  static validateInterestedUsers(interestedUsers: InterestedUser[]): void {
    if (!Array.isArray(interestedUsers)) {
      throw new NotificationValidationError(
        'Interested users must be an array',
        'INVALID_USERS_FORMAT'
      );
    }

    if (interestedUsers.length === 0) {
      throw new NotificationValidationError(
        'No interested users found. Cannot send notifications to empty recipient list.',
        'NO_INTERESTED_USERS'
      );
    }

    // Validate individual user objects
    interestedUsers.forEach((user, index) => {
      this.validateInterestedUser(user, index);
    });

    // Check for duplicate user IDs
    const userIds = interestedUsers.map(u => u.user_id);
    const duplicateIds = userIds.filter((id, index) => userIds.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
      throw new NotificationValidationError(
        'Duplicate user IDs found in interested users list',
        'DUPLICATE_USER_IDS',
        'user_id',
        { duplicateIds }
      );
    }
  }

  /**
   * Validate individual interested user
   */
  static validateInterestedUser(user: InterestedUser, index?: number): void {
    const prefix = index !== undefined ? `User at index ${index}:` : 'User:';

    if (!user.user_id?.trim()) {
      throw new NotificationValidationError(
        `${prefix} User ID is required`,
        'USER_ID_REQUIRED',
        'user_id'
      );
    }

    if (!user.email?.trim()) {
      throw new NotificationValidationError(
        `${prefix} Email address is required`,
        'EMAIL_REQUIRED',
        'email'
      );
    }

    if (!this.isValidEmail(user.email)) {
      throw new NotificationValidationError(
        `${prefix} Invalid email format`,
        'INVALID_EMAIL_FORMAT',
        'email',
        { email: user.email }
      );
    }

    if (!user.username?.trim()) {
      throw new NotificationValidationError(
        `${prefix} Username is required`,
        'USERNAME_REQUIRED',
        'username'
      );
    }

    if (typeof user.interest_level !== 'number' || user.interest_level < 1 || user.interest_level > 10) {
      throw new NotificationValidationError(
        `${prefix} Interest level must be a number between 1 and 10`,
        'INVALID_INTEREST_LEVEL',
        'interest_level',
        { value: user.interest_level }
      );
    }

    if (typeof user.willing_to_pay !== 'boolean') {
      throw new NotificationValidationError(
        `${prefix} Willing to pay must be a boolean value`,
        'INVALID_WILLING_TO_PAY',
        'willing_to_pay'
      );
    }

    if (typeof user.committed_to_buy !== 'boolean') {
      throw new NotificationValidationError(
        `${prefix} Committed to buy must be a boolean value`,
        'INVALID_COMMITTED_TO_BUY',
        'committed_to_buy'
      );
    }
  }

  /**
   * Validate email template data
   */
  static validateEmailTemplate(templateData: EmailTemplateData): void {
    if (!templateData) {
      throw new NotificationValidationError(
        'Email template data is required',
        'TEMPLATE_DATA_REQUIRED'
      );
    }

    if (!Array.isArray(templateData.recipients) || templateData.recipients.length === 0) {
      throw new NotificationValidationError(
        'Email template must have at least one recipient',
        'NO_RECIPIENTS',
        'recipients'
      );
    }

    // Validate all recipient emails
    templateData.recipients.forEach((email, index) => {
      if (!email?.trim()) {
        throw new NotificationValidationError(
          `Recipient email at index ${index} is empty`,
          'EMPTY_RECIPIENT_EMAIL',
          'recipients'
        );
      }
      if (!this.isValidEmail(email)) {
        throw new NotificationValidationError(
          `Invalid recipient email format at index ${index}: ${email}`,
          'INVALID_RECIPIENT_EMAIL',
          'recipients',
          { email, index }
        );
      }
    });

    if (!templateData.subject?.trim()) {
      throw new NotificationValidationError(
        'Email subject line is required',
        'SUBJECT_REQUIRED',
        'subject'
      );
    }

    if (templateData.subject.length > 200) {
      throw new NotificationValidationError(
        'Email subject line is too long (maximum 200 characters)',
        'SUBJECT_TOO_LONG',
        'subject',
        { length: templateData.subject.length, maxLength: 200 }
      );
    }

    if (!templateData.htmlContent?.trim()) {
      throw new NotificationValidationError(
        'Email HTML content is required',
        'HTML_CONTENT_REQUIRED',
        'htmlContent'
      );
    }

    if (!templateData.plainTextContent?.trim()) {
      throw new NotificationValidationError(
        'Email plain text content is required',
        'PLAIN_TEXT_CONTENT_REQUIRED',
        'plainTextContent'
      );
    }

    // Validate project details
    if (!templateData.projectDetails) {
      throw new NotificationValidationError(
        'Email template project details are required',
        'PROJECT_DETAILS_REQUIRED',
        'projectDetails'
      );
    }

    if (!templateData.projectDetails.title?.trim()) {
      throw new NotificationValidationError(
        'Project title in template details is required',
        'PROJECT_TITLE_REQUIRED',
        'projectDetails.title'
      );
    }
  }

  /**
   * Validate website message data
   */
  static validateWebsiteMessage(messageData: WebsiteMessageData): void {
    if (!messageData) {
      throw new NotificationValidationError(
        'Website message data is required',
        'MESSAGE_DATA_REQUIRED'
      );
    }

    if (!Array.isArray(messageData.recipients) || messageData.recipients.length === 0) {
      throw new NotificationValidationError(
        'Website message must have at least one recipient',
        'NO_RECIPIENTS',
        'recipients'
      );
    }

    // Validate recipient user IDs
    messageData.recipients.forEach((userId, index) => {
      if (!userId?.trim()) {
        throw new NotificationValidationError(
          `Recipient user ID at index ${index} is empty`,
          'EMPTY_RECIPIENT_ID',
          'recipients'
        );
      }
    });

    if (!messageData.messageText?.trim()) {
      throw new NotificationValidationError(
        'Message text is required',
        'MESSAGE_TEXT_REQUIRED',
        'messageText'
      );
    }

    if (messageData.messageText.length > 1000) {
      throw new NotificationValidationError(
        'Message text is too long (maximum 1000 characters)',
        'MESSAGE_TEXT_TOO_LONG',
        'messageText',
        { length: messageData.messageText.length, maxLength: 1000 }
      );
    }

    if (!messageData.projectId?.trim()) {
      throw new NotificationValidationError(
        'Project ID is required for website messages',
        'PROJECT_ID_REQUIRED',
        'projectId'
      );
    }

    if (!['project_release', 'discount_offer', 'update'].includes(messageData.messageType)) {
      throw new NotificationValidationError(
        'Invalid message type. Must be one of: project_release, discount_offer, update',
        'INVALID_MESSAGE_TYPE',
        'messageType',
        { 
          value: messageData.messageType, 
          validTypes: ['project_release', 'discount_offer', 'update'] 
        }
      );
    }

    if (typeof messageData.includeProjectLink !== 'boolean') {
      throw new NotificationValidationError(
        'Include project link must be a boolean value',
        'INVALID_INCLUDE_PROJECT_LINK',
        'includeProjectLink'
      );
    }
  }

  /**
   * Validate custom message content
   */
  static validateCustomMessage(message: string, maxLength: number = 1000): void {
    if (!message?.trim()) {
      throw new NotificationValidationError(
        'Custom message content is required',
        'CUSTOM_MESSAGE_REQUIRED',
        'customMessage'
      );
    }

    if (message.length > maxLength) {
      throw new NotificationValidationError(
        `Custom message is too long (maximum ${maxLength} characters)`,
        'CUSTOM_MESSAGE_TOO_LONG',
        'customMessage',
        { length: message.length, maxLength }
      );
    }

    // Check for potentially harmful content
    const suspiciousPatterns = [
      /<script[^>]*>/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe[^>]*>/i,
      /<object[^>]*>/i,
      /<embed[^>]*>/i
    ];

    for (const pattern of suspiciousPatterns) {
      if (pattern.test(message)) {
        throw new NotificationValidationError(
          'Custom message contains potentially unsafe content',
          'UNSAFE_MESSAGE_CONTENT',
          'customMessage',
          { pattern: pattern.toString() }
        );
      }
    }
  }

  /**
   * Validate email address format
   */
  static isValidEmail(email: string): boolean {
    if (!email || typeof email !== 'string') return false;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Validate notification preferences parameters
   */
  static validateNotificationParams(
    notificationType: string,
    projectId?: string,
    publisherId?: string
  ): void {
    if (!notificationType?.trim()) {
      throw new NotificationValidationError(
        'Notification type is required',
        'NOTIFICATION_TYPE_REQUIRED',
        'notificationType'
      );
    }

    if (!['email_template', 'website_message', 'discount_offer'].includes(notificationType)) {
      throw new NotificationValidationError(
        'Invalid notification type',
        'INVALID_NOTIFICATION_TYPE',
        'notificationType',
        { 
          value: notificationType, 
          validTypes: ['email_template', 'website_message', 'discount_offer'] 
        }
      );
    }

    if (projectId && !projectId.trim()) {
      throw new NotificationValidationError(
        'Project ID cannot be empty when provided',
        'EMPTY_PROJECT_ID',
        'projectId'
      );
    }

    if (publisherId && !publisherId.trim()) {
      throw new NotificationValidationError(
        'Publisher ID cannot be empty when provided',
        'EMPTY_PUBLISHER_ID',
        'publisherId'
      );
    }
  }

  /**
   * Validate bulk operation parameters
   */
  static validateBulkOperationParams(
    batchSize: number,
    totalItems: number
  ): void {
    if (typeof batchSize !== 'number' || batchSize <= 0) {
      throw new NotificationValidationError(
        'Batch size must be a positive number',
        'INVALID_BATCH_SIZE',
        'batchSize',
        { value: batchSize }
      );
    }

    if (batchSize > 50) {
      throw new NotificationValidationError(
        'Batch size is too large (maximum 50 items per batch)',
        'BATCH_SIZE_TOO_LARGE',
        'batchSize',
        { value: batchSize, maxBatchSize: 50 }
      );
    }

    if (typeof totalItems !== 'number' || totalItems < 0) {
      throw new NotificationValidationError(
        'Total items must be a non-negative number',
        'INVALID_TOTAL_ITEMS',
        'totalItems',
        { value: totalItems }
      );
    }
  }

  /**
   * Sanitize user input for notifications
   */
  static sanitizeUserInput(input: string): string {
    if (!input || typeof input !== 'string') return '';
    
    return input
      .trim()
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  /**
   * Validate rate limiting parameters
   */
  static validateRateLimiting(
    operationsPerSecond: number,
    operationsPerMinute: number
  ): void {
    if (typeof operationsPerSecond !== 'number' || operationsPerSecond <= 0) {
      throw new NotificationValidationError(
        'Operations per second must be a positive number',
        'INVALID_RATE_LIMIT_SECONDS',
        'operationsPerSecond'
      );
    }

    if (typeof operationsPerMinute !== 'number' || operationsPerMinute <= 0) {
      throw new NotificationValidationError(
        'Operations per minute must be a positive number',
        'INVALID_RATE_LIMIT_MINUTES',
        'operationsPerMinute'
      );
    }

    if (operationsPerSecond > 10) {
      throw new NotificationValidationError(
        'Rate limit is too high (maximum 10 operations per second)',
        'RATE_LIMIT_TOO_HIGH',
        'operationsPerSecond',
        { value: operationsPerSecond, maxRate: 10 }
      );
    }
  }
}

/**
 * Error recovery utilities for notification operations
 */
export class NotificationErrorRecovery {
  /**
   * Attempt to recover from database connection errors
   */
  static async retryDatabaseOperation<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delayMs: number = 1000
  ): Promise<T> {
    let lastError: Error;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error: any) {
        lastError = error;
        
        // Don't retry validation errors or user permission errors
        if (error instanceof NotificationValidationError || 
            error.code === 'PGRST116' || // RLS violation
            error.code === 'PGRST301') { // JWT expired
          throw error;
        }
        
        if (attempt < maxRetries) {
          console.warn(`Database operation failed (attempt ${attempt}/${maxRetries}):`, error.message);
          await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
        }
      }
    }
    
    throw new NotificationServiceError(
      `Database operation failed after ${maxRetries} attempts: ${lastError!.message}`,
      'DATABASE_OPERATION_FAILED',
      'retryDatabaseOperation',
      { maxRetries, originalError: lastError!.message }
    );
  }

  /**
   * Handle partial failures in bulk operations
   */
  static handlePartialFailures<T>(
    results: Array<{ success: boolean; data?: T; error?: Error; id?: string }>,
    operationName: string
  ): { successes: T[]; failures: Array<{ id?: string; error: Error }> } {
    const successes: T[] = [];
    const failures: Array<{ id?: string; error: Error }> = [];

    results.forEach(result => {
      if (result.success && result.data) {
        successes.push(result.data);
      } else if (result.error) {
        failures.push({ id: result.id, error: result.error });
      }
    });

    if (failures.length > 0) {
      console.warn(`${operationName} completed with ${failures.length} failures out of ${results.length} operations`);
    }

    return { successes, failures };
  }

  /**
   * Generate user-friendly error messages
   */
  static getUserFriendlyErrorMessage(error: Error): string {
    if (error instanceof NotificationValidationError) {
      switch (error.code) {
        case 'PROJECT_REQUIRED':
          return 'Project information is missing. Please try again.';
        case 'NO_INTERESTED_USERS':
          return 'No users have expressed interest in this project yet. Try again after users show interest.';
        case 'INVALID_EMAIL_FORMAT':
          return 'One or more email addresses are invalid. Please check the recipient list.';
        case 'MESSAGE_TEXT_REQUIRED':
          return 'Please enter a message to send.';
        case 'SUBJECT_REQUIRED':
          return 'Please provide an email subject line.';
        case 'UNSAFE_MESSAGE_CONTENT':
          return 'Your message contains content that cannot be sent. Please revise your message.';
        default:
          return error.message;
      }
    }

    if (error instanceof NotificationServiceError) {
      switch (error.code) {
        case 'DATABASE_OPERATION_FAILED':
          return 'There was a problem connecting to the database. Please try again in a moment.';
        case 'RATE_LIMIT_EXCEEDED':
          return 'Too many requests. Please wait a moment before trying again.';
        case 'PERMISSION_DENIED':
          return 'You do not have permission to perform this action.';
        default:
          return 'An unexpected error occurred. Please try again.';
      }
    }

    // Generic error handling
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'Network connection error. Please check your internet connection and try again.';
    }

    if (error.message.includes('timeout')) {
      return 'The operation timed out. Please try again.';
    }

    return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }
}
